import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2,
    Mail,
    MapPin,
    MessageCircle,
    Phone,
    Send,
    User,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { C, COMPANY } from "@/data/content";
import Reveal, { SectionHeader } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = {
    name: "",
    company: "",
    country: "",
    phone: "",
    email: "",
    service: "",
    message: "",
};

export default function Contact() {
    const { L } = useLanguage();
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("idle");
    const F = C.contact.form;

    useEffect(() => {
        const handler = (e) =>
            setForm((f) => ({ ...f, service: e.detail }));
        window.addEventListener("llb-request-service", handler);
        return () => window.removeEventListener("llb-request-service", handler);
    }, []);

    const waHref = `${COMPANY.waHref}?text=${encodeURIComponent(
        L(C.contact.waPrefill)
    )}`;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await axios.post(`${API}/contact`, form);
            toast.success(L(F.success));
            setForm(EMPTY);
        } catch {
            toast.error(L(F.error));
        } finally {
            setStatus("idle");
        }
    };

    const inputCls =
        "w-full rounded-xl border border-gold-line bg-ink/60 px-4 py-3 text-sm text-txt-1 placeholder:text-txt-3 outline-none transition-colors focus:border-gold-line-strong";
    const labelCls = "mb-2 block text-sm text-txt-2";

    return (
        <section
            id="contact"
            className="border-t border-gold-line/40 bg-ink-2/40 py-24 sm:py-32"
        >
            <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <SectionHeader
                        eyebrow={L(C.contact.eyebrow)}
                        title={L(C.contact.title)}
                    />
                    <Reveal delay={0.12}>
                        <p className="mt-8 leading-relaxed text-txt-2">
                            {L(C.contact.text)}
                        </p>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <a
                                href={waHref}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-whatsapp-button"
                                className="flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-[#06230F] transition-transform hover:scale-105"
                            >
                                <MessageCircle className="size-4" />
                                {L(C.contact.whatsappCta)}
                            </a>
                            <a
                                href={COMPANY.telHref}
                                data-testid="contact-call-button"
                                className="flex items-center gap-2 rounded-full border border-gold-line-strong px-6 py-3 text-sm font-semibold text-brass transition-colors hover:bg-brass/10"
                            >
                                <Phone className="size-4" />
                                {L(C.contact.callCta)}
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.26}>
                        <ul className="mt-10 space-y-4 border-t border-gold-line/50 pt-8 text-sm">
                            <li className="flex items-start gap-3" data-testid="contact-address-row">
                                <MapPin className="mt-0.5 size-4 shrink-0 text-brass" />
                                <span dir="auto" className="text-txt-1">
                                    {L(COMPANY.address)}
                                </span>
                            </li>
                            {COMPANY.phones.map((p, i) => (
                                <li key={p.href} className="flex items-center gap-3" data-testid={`contact-phone-row-${i + 1}`}>
                                    <Phone className="size-4 shrink-0 text-brass" />
                                    <a href={p.href} dir="ltr" className="text-txt-1 transition-colors hover:text-brass">
                                        {p.display}
                                    </a>
                                </li>
                            ))}
                            <li className="flex items-center gap-3" data-testid="contact-email-row">
                                <Mail className="size-4 shrink-0 text-brass" />
                                <a href={COMPANY.emailHref} dir="ltr" className="text-txt-1 transition-colors hover:text-brass">
                                    {COMPANY.email}
                                </a>
                            </li>
                            <li className="flex items-center gap-3" data-testid="contact-person-row">
                                <User className="size-4 shrink-0 text-brass" />
                                <span className="text-txt-3">{L(C.contact.personLabel)}:</span>
                                <span className="text-txt-1">{COMPANY.contactPerson}</span>
                            </li>
                        </ul>
                    </Reveal>
                </div>

                <Reveal delay={0.15} className="lg:col-span-7">
                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="glass rounded-2xl border border-gold-line p-8"
                    >
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label className={labelCls}>{L(F.name)}</label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    data-testid="contact-form-name-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{L(F.company)}</label>
                                <input
                                    value={form.company}
                                    onChange={set("company")}
                                    data-testid="contact-form-company-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{L(F.country)}</label>
                                <input
                                    value={form.country}
                                    onChange={set("country")}
                                    data-testid="contact-form-country-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{L(F.phone)}</label>
                                <input
                                    required
                                    dir="ltr"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    data-testid="contact-form-phone-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{L(F.email)}</label>
                                <input
                                    type="email"
                                    dir="ltr"
                                    value={form.email}
                                    onChange={set("email")}
                                    data-testid="contact-form-email-input"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{L(F.service)}</label>
                                <select
                                    value={form.service}
                                    onChange={set("service")}
                                    data-testid="contact-form-service-select"
                                    className={`${inputCls} appearance-none`}
                                >
                                    <option value="">{L(F.servicePlaceholder)}</option>
                                    {C.services.items.map((s) => (
                                        <option key={s.id} value={L(s.title)}>
                                            {L(s.title)}
                                        </option>
                                    ))}
                                    <option value={L(F.serviceOther)}>
                                        {L(F.serviceOther)}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className={labelCls}>{L(F.message)}</label>
                            <textarea
                                required
                                rows={5}
                                value={form.message}
                                onChange={set("message")}
                                placeholder={L(F.msgPlaceholder)}
                                data-testid="contact-form-message-input"
                                className={`${inputCls} resize-none`}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={status === "sending"}
                            data-testid="contact-form-submit-button"
                            className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-brass px-7 py-3.5 text-sm font-bold text-ink transition-transform hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
                        >
                            {status === "sending" ? (
                                <>
                                    <Loader2 className="size-4 animate-spin" />
                                    {L(F.sending)}
                                </>
                            ) : (
                                <>
                                    <Send className="size-4" />
                                    {L(F.submit)}
                                </>
                            )}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
